import * as THREE from 'three';
import { G, M, TINT, HP, K_BEV, glowMat, mesh, bevel, plate, rib, seam, cable, joint, ram, mergeBones, stages, boneG } from './kit';
/* ============================================================
   CHIMÄRE 奇美拉 —— 最終・八家族複合
   ============================================================

   剪影：錐楔壁勺球桿翼環全長在一根脊椎上
   階段：'P1 八家族全在','P2 楔壁勺桿四肢剝落','P3 只剩球與環'

   由 `tools/port-bosses.mjs` 從 `enemy_sample_ani.html` 機械移植，
   **不要手改這個檔**：要改造型請改原型再重跑移植器，否則 1:1 就斷了。
   驗證：`node tools/port-bosses.mjs --check`。
   ⚠️ 比對只守得住數字與字串，**動畫對不對只能實看**（圖鑑的開發模式）。 */


/* ── BOSS-25 CHIMÄRE 奇美拉 ── 八個剪影家族接在同一根脊椎上。
   錐、楔、壁、勺、球、桿、翼、環 —— 玩家整場遊戲學的那八個外框，
   全部長在同一隻身上。這是家族系統唯一一次被當成**內容**而不是規則來用。
   每過一階段掉兩個家族，最後只剩球與環。 */
var CHF = ['spike','wedge','wall','ladle','orb','bar','wing','ring'];
export function buildChimare(){
  var GLV = glowMat(TINT.vio.hex, 2.4), GL = glowMat(TINT.red.hex, 2.1),
      GLA = glowMat(TINT.amb.hex, 2.2);
  var root = new THREE.Group(), limbs: any = {}, i;
  var spine = boneG(root, 0, 2.40, 0);
  spine.add(bevel(M.dark, 3.40, 3.00, 3.40, [0, 0, 0]));
  var s2b = boneG(spine, 0, 2.60, 0);
  s2b.add(mesh(G.bev, M.armorF, [2.90*K_BEV, 5.00, 2.90*K_BEV], [0, 2.50, 0]));
  s2b.add(rib(M.armorF, 0.22, 4.60, 0.22, [0, 2.50, 1.50]));
  s2b.add(seam(2.40, 0.12, 0.24, [0, 0.40, 1.48]));
  s2b.add(seam(2.20, 0.12, 0.24, [0, 4.60, 1.42]));
  s2b.add(cable([1.30, 1.00, -1.30], 1.60));               // 脊椎外側的線束（單邊）
  var s3b = boneG(s2b, 0, 5.00, 0);
  s3b.add(mesh(G.bev, M.armorF, [2.30*K_BEV, 4.20, 2.30*K_BEV], [0, 2.10, 0]));
  /* 錐：前指的巨刺 */
  var L = boneG(s2b, 1.90, 2.60, 1.20); L.rotation.y = -0.34;
  L.add(joint(0.62, [0,0,0], true));                       // 接口：軸端外露
  L.add(ram([0.72, 0.10, 1.10], 1.40, 0.22, 0.18, [HP-0.2,0,0]));
  L.add(mesh(G.cyl, M.armorF, [1.10,4.60,1.10], [0,0,2.30], [HP,0,0]));
  L.add(mesh(G.cone, M.armorF, [1.30,3.60,1.30], [0,0,6.20], [HP,0,0]));
  var g1 = mesh(G.tor, GL, [1.60,1.60,0.55], [0,0,4.40]); g1.userData.noMerge = 1; L.add(g1);
  limbs.spike = L;
  /* 楔：貼地斜面 */
  L = boneG(spine, 0, -1.40, 2.20);
  L.add(plate(M.armorF, 6.40, 3.00, 0.90, [0, 0, 0], -0.72));
  L.add(rib(M.armorF, 6.50, 0.20, 0.20, [0, 0.90, 0.40], -0.72));
  limbs.wedge = L;
  /* 壁：直立盾 */
  L = boneG(s2b, -3.20, 2.40, 0.60); L.rotation.y = 0.30;
  L.add(joint(0.56, [0.90, 0, 0], true));
  L.add(plate(M.armorF, 3.20, 6.40, 0.80, [0, 0, 0], 0));
  L.add(seam (2.80, 0.14, 0.30, [0, 1.60, 0.46]));
  L.add(seam (2.80, 0.14, 0.30, [0,-1.60, 0.46]));
  L.add(rib(M.armorF, 0.22, 6.00, 0.22, [ 1.45, 0, 0.40]));
  L.add(rib(M.armorF, 0.22, 6.00, 0.22, [-1.45, 0, 0.40]));
  limbs.wall = L;
  /* 勺：上翹臼砲 */
  L = boneG(s2b, 2.40, 4.60, -1.20); L.rotation.z = -0.30;
  L.add(joint(0.50, [0,0,0], true));
  L.add(mesh(G.cyl, M.dark, [1.60,4.80,1.60], [0, 2.20, 0.90], [-0.46,0,0]));
  L.add(ram([0.86, 1.60, 0.50], 1.80, 0.20, 0.16, [-0.46,0,0]));
  var g2 = mesh(G.tor, GLV, [2.10,2.10,0.55], [0, 4.30, 1.90], [-0.46,0,0]);
  g2.userData.noMerge = 1; L.add(g2);
  limbs.ladle = L;
  /* 球：頭 */
  L = boneG(s3b, 0, 4.40, 0);
  L.add(mesh(G.oct, M.dark, [3.20,3.20,3.20], [0,0,0]));
  L.add(mesh(G.tor, M.armorF, [3.90,3.90,0.50], [0,0,0], [HP,0,0]));
  var g3 = mesh(G.oct, GLV, [1.50,1.50,1.50], [0, 0, 1.30]);
  g3.userData.noMerge = 1; L.add(g3);
  limbs.orb = L;
  /* 桿：水平長刃 */
  L = boneG(s2b, 0, 4.00, -1.80);
  L.add(joint(0.46, [0,0,0], true));
  L.add(mesh(G.box, M.armorF, [9.60,0.40,1.20], [0,0,0]));
  L.add(rib (M.armorF, 9.20, 0.16, 0.16, [0, 0.26, 0.42]));
  L.add(mesh(G.cone, M.armorF, [0.90,1.80,0.90], [ 4.90,0,0], [0,0,-HP]));
  L.add(mesh(G.cone, M.armorF, [0.90,1.80,0.90], [-4.90,0,0], [0,0, HP]));
  limbs.bar = L;
  /* 翼：後掠雙翼 */
  L = boneG(s3b, 0, 2.20, -1.00);
  for (i = 0; i < 2; i++){
    var sg = i ? -1 : 1;
    L.add(plate(M.armorF, 5.40, 0.30, 2.40, [sg*3.40, 0.30, -0.60], 0, -sg*0.34, -sg*0.26));
  }
  limbs.wing = L;
  /* 環：冠 */
  L = boneG(s3b, 0, 5.60, 0);
  L.add(mesh(G.tor, M.armorF, [6.40,6.40,0.60], [0,0,0], [HP,0,0]));
  var g4 = mesh(G.tor, GLA, [6.90,6.90,0.30], [0,0,0], [HP,0,0]);
  g4.userData.noMerge = 1; L.add(g4);
  limbs.ring = L;

  root.userData.rig = {spine:spine, sp2:s2b, sp3:s3b, limbs:limbs,
                       g1:g1, g2:g2, g3:g3, g4:g4, GLV:GLV, GL:GL, GLA:GLA};
  return mergeBones(root);
}
export function updChimare(rig, o, dt){
  stages(rig, o, dt, 1.6);
  var s1 = rig.s1, s2 = rig.s2, s3 = rig.s3, i;
  /* 每階段掉兩個家族。最後留下球與環 —— 那也正好是最不像機械的兩個。 */
  var gone = {wedge:s1, wall:s1, ladle:s1, bar:s1, wing:s2, spike:s2};
  for (i = 0; i < CHF.length; i++){
    var k = CHF[i], L = rig.limbs[k];
    var g = gone[k] === undefined ? 0 : gone[k];
    L.visible = g < 0.5;
    L.scale.setScalar(1 - 0.4*g);
  }
  rig.limbs.spike.rotation.x = -0.20 + o.tel*0.34 - o.atk*0.60;
  rig.limbs.ladle.rotation.x = -o.tel*0.40 + o.atk*0.26;
  rig.limbs.bar.rotation.y += dt*(0.5 + 2.6*o.atk + 1.4*s1);
  rig.limbs.ring.rotation.z += dt*(0.4 + 1.2*s1 + 2.6*s2);
  rig.limbs.orb.rotation.y = 0.46*Math.sin(o.t*0.8) + 0.9*s2*Math.sin(o.t*2.0);
  rig.spine.position.y = 2.40 + 0.20*Math.sin(o.ph*2*Math.PI * 2)*o.mv - 0.60*s2;
  rig.sp2.rotation.x = 0.05*Math.sin(o.t*0.7) - o.tel*0.10 + 0.16*s2;
  rig.sp3.rotation.x = -0.32*s2;
  rig.sp3.rotation.y = 0.30*Math.sin(o.t*0.6)*(1 + s2);
  rig.g3.scale.setScalar((1 + 0.6*s2)*(1 + 0.16*Math.sin(o.t*3) + 0.9*o.tel));
  rig.GL.emissiveIntensity  = 2.1 + 2.6*o.tel + 5*o.atk*(1-o.atk);
  rig.GLV.emissiveIntensity = 2.4 + 3.0*s2 + 2.6*o.tel;
  rig.GLA.emissiveIntensity = 2.2 + 2.0*s2 + 2.2*o.tel;
}